import { useState } from "react";
import "../styles/HomeNew.css";
import projectData from "../data/projectData.json";

// ── Metadata maps (mirrors Home.jsx) ────────────────────────────────────────

const G_META = {
  "solidarity-engineering": { focus: "Clean Water / Health", region: "Americas", color: "lchip--purple" },
  "action-in-africa": { focus: "Education", region: "Uganda", color: "lchip--pink" },
  "mali-rising": { focus: "Girls' Education", region: "West Africa", color: "lchip--green" },
  "connected": { focus: "Education Access", region: "Central America", color: "lchip--purple" },
  "el-porvenir": { focus: "Clean Water", region: "Nicaragua", color: "lchip--pink" },
  "maya-book-project": { focus: "Literacy", region: "Guatemala", color: "lchip--green" },
  "safe-passage": { focus: "Health / Education", region: "Guatemala", color: "lchip--purple" },
  "yabal": { focus: "Women Empowerment", region: "Guatemala", color: "lchip--pink" },
  "ihf": { focus: "Child Education", region: "Guatemala", color: "lchip--green" },
  "kyc": { focus: "Youth Health", region: "Kenya", color: "lchip--purple" },
};

const L_META = {
  "b2s": { focus: "Education", color: "lchip--purple" },
  "one-hope-project": { focus: "Health", color: "lchip--pink" },
  "ywca": { focus: "Equity", color: "lchip--green" },
  "creative-healing": { focus: "Mental Health", color: "lchip--purple" },
  "bgc": { focus: "Youth", color: "lchip--pink" },
  "art-station": { focus: "Arts", color: "lchip--green" },
  "sunnyside-community-garden": { focus: "Food Security", color: "lchip--purple" },
  "charm": { focus: "Community", color: "lchip--pink" },
  "project-oz": { focus: "Youth Services", color: "lchip--green" },
  "dreams-are-possible": { focus: "Community", color: "lchip--purple" },
  "path": { focus: "Crisis Support", color: "lchip--pink" },
};

// ── Card components ──────────────────────────────────────────────────────────

function GlobalCard({ proj }) {
  const [open, setOpen] = useState(false);
  const m = G_META[proj.uriName] || { focus: "Philanthropy", region: "Global", color: "lchip--purple" };

  return (
    <div className={`lcard${open ? " lcard--open" : ""}`}>
      <div className="lcard__top">
        <div style={{ display: "flex", flexDirection: "column", gap: ".25rem" }}>
          <span className={`lcard__chip ${m.color}`}>{m.focus}</span>
          <span style={{ fontSize: ".65rem", fontWeight: 700, color: "var(--lt)", letterSpacing: ".06em", textTransform: "uppercase" }}>
            {m.region}
          </span>
        </div>
        <a href={`/project/${proj.uriName}`}>
          <div className="lcard__logo">
            <img
              src={`/images/projects/${proj.logo}`}
              alt=""
              loading="lazy"
              onError={e => (e.target.style.display = "none")}
            />
          </div>
        </a>
      </div>

      <h3 className="lcard__name">
        <a href={`/project/${proj.uriName}`} style={{ color: "inherit", textDecoration: "none" }}>
          {proj.name}
        </a>
      </h3>

      <p className="lcard__desc">{proj.shortDesc}</p>

      {proj.afs && (
        <span style={{
          alignSelf: "flex-start",
          fontSize: ".7rem",
          fontWeight: 700,
          color: "var(--pu-700)",
          background: "var(--pu-100)",
          borderRadius: "100px",
          padding: ".28em .8em",
        }}>
          Approved Support: ${proj.afs.toLocaleString()}
        </span>
      )}

      {open && (
        <div className="lcard__extra">
          {proj.desc && proj.desc !== proj.shortDesc && (
            <p dangerouslySetInnerHTML={{ __html: proj.desc }} />
          )}
          <a href={proj.uri} target="_blank" rel="noopener noreferrer">
            Visit website ↗
          </a>
        </div>
      )}

      <button className="lcard__toggle" onClick={() => setOpen(v => !v)}>
        {open ? "Show less ↑" : "Read more ↓"}
      </button>
    </div>
  );
}

function LocalCard({ proj }) {
  const [open, setOpen] = useState(false);
  const m = L_META[proj.uriName] || { focus: "Community", color: "lchip--purple" };

  return (
    <div className={`lcard${open ? " lcard--open" : ""}`}>
      <div className="lcard__top">
        <span className={`lcard__chip ${m.color}`}>{m.focus}</span>
        <a href={`/project/${proj.uriName}`}>
          <div className="lcard__logo">
            <img
              src={`/images/projects/${proj.logo}`}
              alt=""
              loading="lazy"
              onError={e => (e.target.style.display = "none")}
            />
          </div>
        </a>
      </div>

      <h3 className="lcard__name">
        <a href={`/project/${proj.uriName}`} style={{ color: "inherit", textDecoration: "none" }}>
          {proj.name}
        </a>
      </h3>

      <p className="lcard__desc">{proj.shortDesc}</p>

      {open && (
        <div className="lcard__extra">
          {proj.desc && proj.desc !== proj.shortDesc && (
            <p dangerouslySetInnerHTML={{ __html: proj.desc }} />
          )}
          <a href={proj.uri} target="_blank" rel="noopener noreferrer">
            Visit website ↗
          </a>
        </div>
      )}

      <button className="lcard__toggle" onClick={() => setOpen(v => !v)}>
        {open ? "Show less ↑" : "Read more ↓"}
      </button>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function Projects() {
  const global = projectData.projects.filter(p => p.type === "G");
  const local = projectData.projects.filter(p => p.type === "L");

  return (
    <div className="pg">

      {/* ── HERO ── */}
      <section className="hero" style={{ minHeight: "40svh" }}>
        <div className="hero__glow hero__glow--1" />
        <div className="hero__glow hero__glow--2" />
        <div className="hero__glow hero__glow--3" />
        <div className="hero__inner" style={{ gridTemplateColumns: "1fr" }}>
          <div className="hero__text">
            <p className="hero__kicker">For A Better Tomorrow</p>
            <h1 className="hero__h1">
              Our <span className="hero__accent">Projects</span>
            </h1>
            <p className="hero__sub">
              FBT-supported initiatives spanning education, water, health, and
              economic development — locally and around the world.
            </p>
            <div className="hero__ctas">
              <a href="#global" className="cta cta--solid">Global Projects</a>
              <a href="#local" className="cta cta--ghost">Local Projects ↓</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── GLOBAL PROJECTS ── */}
      <section id="global" className="local">
        <div className="sec-hd">
          <span className="sec-eye">Around the World</span>
          <h2 className="sec-h2">Global Projects</h2>
          <p className="sec-sub">
            Here are the global projects previously supported by For A Better Tomorrow.
          </p>
        </div>
        <div className="local__grid">
          {global.map(p => <GlobalCard key={p.uriName} proj={p} />)}
        </div>
      </section>

      {/* ── DIVIDER QUOTE ── */}
      <div className="quote-band">
        <p>"FBT believes in hope — and hope for a better tomorrow."</p>
      </div>

      {/* ── LOCAL PROJECTS ── */}
      <section id="local" className="local">
        <div className="sec-hd">
          <span className="sec-eye">Close to Home</span>
          <h2 className="sec-h2">Local Projects</h2>
          <p className="sec-sub">
            Here are the local projects currently supported by For A Better Tomorrow.
          </p>
        </div>
        <div className="local__grid">
          {local.map(p => <LocalCard key={p.uriName} proj={p} />)}
        </div>
      </section>

    </div>
  );
}